import { useEffect } from 'react';
import Typography from '@components/common/typography';
import Input from '@components/common/input';
import Button from '@components/common/button';
import styles from './styles.module.scss';
import useSignupContext from '@hooks/context/useSignupContext';
import { useFormContext, useWatch } from 'react-hook-form';
import FormField from '@components/form_field';
import { apiRegistrationService } from '@api';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { passwordValidation } from '@utils/validation';
import passwordIllustration from '@svg/illustrations/auth/password.svg';
import { EUserType } from '@xeppt/xeppt-sdk/types';
import { useQueryParams } from '@hooks/helpers/useQueryParams';

const PasswordSection = () => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'signup.password'
    });
    const [ref] = useQueryParams(['ref']);
    const { labelLocale, submitLocale, requestErrorLocale, validationLocale, placeholderLocale } =
        useLocales();
    const { nextStep, handleChangeIllustration, prevStep } = useSignupContext();
    const { getValues, trigger, setValue, control } = useFormContext();
    const type = useWatch({ name: 'type', control });

    useEffect(() => {
        handleChangeIllustration(passwordIllustration);
    }, []);

    const { handleRequest: handleActivate, isLoading } = useApiMutation({
        method: () => {
            const { phone_number, phone_code, email, password, password_confirm, type } =
                getValues();
            return apiRegistrationService.registerAccount({
                password,
                password_confirm,
                phone_code,
                email,
                type,
                phone_number,
                ref: ref || undefined
            });
        },
        onSuccess: (data) => {
            nextStep();
            setValue('user_id', data.id);
        },
        onError: requestErrorLocale
    });

    const onSubmit = () => {
        trigger().then((isValid) => {
            if (isValid) {
                handleActivate(undefined);
            }
        });
    };

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.steps}>STEP 5/{type === EUserType.BUSINESS ? '11' : '7'}</div>
            <Typography className={styles.title} variant="h3">
                {t('title')}
            </Typography>
            <div className={styles.form}>
                <FormField
                    name="password"
                    renderComponent={({ value, ...props }) => (
                        <Input
                            {...props}
                            label={labelLocale('password')}
                            full
                            value={value}
                            placeholder={placeholderLocale('enter_password')}
                            type="password"
                            required
                            passwordErrors={
                                !!value ? passwordValidation(value, validationLocale) : undefined
                            }
                        />
                    )}
                />
                <FormField
                    name="password_confirm"
                    renderComponent={(props) => (
                        <Input
                            {...props}
                            label={labelLocale('confirm_password')}
                            full
                            placeholder={placeholderLocale('enter_confirm_password')}
                            type="password"
                            required
                            disablePaste
                        />
                    )}
                />
                <div className={styles.actions}>
                    <Button
                        className={styles.back}
                        leftIcon="arrow_left"
                        variant="outlined-primary"
                        onClick={() => prevStep()}>
                        {submitLocale('back')}
                    </Button>
                    <Button
                        className={styles.next}
                        onClick={onSubmit}
                        rightIcon="arrow_right"
                        isLoading={isLoading}
                        variant="primary">
                        {submitLocale('next')}
                    </Button>
                </div>
            </div>
        </motion.div>
    );
};

export default PasswordSection;
